<template>
  <div class="wette-page">
    <div v-if="loading" class="state-box">Lade Wette...</div>

    <div v-else-if="error" class="state-box error">
      {{ error }}
    </div>

    <template v-else-if="bet">
      <section class="bet-card">
        <h1>{{ bet.title }}</h1>
        <p class="description">{{ bet.description }}</p>

        <div class="meta">
          <span v-if="bet.running"><strong>Läuft gerade:</strong> ja</span>
          <span v-else><strong>Läuft gerade:</strong> nein</span>
          <span v-if="bet.running" class="countdown">
            Ende in: {{ countdownText }}
          </span>
          <span v-if="bet.settled"><strong>Verrechnet:</strong> ja</span>
          <span v-else><strong>Verrechnet:</strong> noch nicht</span>
        </div>
        <div v-if="bet.settled" class="result-box" :class="resultClass">
          Ergebnis: {{ resultTitle }}
        </div>
      </section>

      <section class="bet-card">
        <h2>Positionen</h2>

        <div class="positions-grid">
          <button v-for="pos in bet.positions" :key="pos.id" type="button" class="position-card" :class="positionClass(pos)" @click="selectedPosition = pos" :disabled="bet.running === false">
            <div class="position-title">{{ pos.title }}</div>
            <div class="position-id">{{ formatMoney(pos.balance) }} Gesamt</div>
          </button>
        </div>
      </section>

      <section class="bet-card">
        <h2>Einsatz setzen</h2>

        <div v-if="!isLoggedIn" class="hint">
          Du musst eingeloggt sein, um an der Wette teilzunehmen.
           <RouterLink to="/login" class="login-button">Zur Anmeldung</RouterLink>
        </div>

        <div v-else-if="!bet.running" class="hint">
          Das Wettfenster ist geschlossen.
        </div>

        <div v-else-if="isBettingDisallowed" class="hint error-hint">
          {{ bettingDisallowedReason }}
        </div>

        <form v-else @submit.prevent="handlePlaceBet">
          <div class="form-group">
            <label>Ausgewählte Position</label>
            <input :value="selectedPosition ? selectedPosition.title : 'Keine Position gewählt'" disabled/>
          </div>

          <div class="form-group">
            <label for="balance">Einsatz</label>
            <input id="balance" v-model="balance" type="number" min="0.01" step="0.01" required />
          </div>

          <button type="submit" :disabled="saving || !selectedPosition || isBettingDisallowed">
            Wette setzen
          </button>
        </form>
      </section>

      <section v-if="bet.settled && enrichedParticipants.length" class="bet-card participants-section">
        <h2>Teilnehmer & Ergebnisse</h2>
        <ul class="participants-list">
          <li
            v-for="p in enrichedParticipants"
            :key="p.email"
            class="participant-item"
            :class="{ 'participant-winner': p.isWinner, 'participant-loser': !p.isWinner }"
          >
            <div class="participant-info">
              <span class="participant-email">{{ p.email }}</span>
              <span class="participant-position">setzte auf: {{ p.positionTitles }}</span>
            </div>
            <div class="participant-stake">
              {{ p.totalStake }} €
            </div>
          </li>
        </ul>
      </section>

      <BetMyStakesTable :my-enriched-bets="myEnrichedBets" :total-stake="bet.myTotal" @increase="handleIncreaseStake" />

      <p v-if="successMessage" class="message success">{{ successMessage }}</p>
      <p v-if="actionError" class="message error">{{ actionError }}</p>

      <section ref="chatSectionRef" class="bet-card bet-chat">
        <h2>Kommentare</h2>
        <div class="chat-container">
          <ChatView v-if="bet && isChatVisible" :chatIdProp="bet.id" />
        </div>
      </section>
    </template>
  </div>
</template>

<script setup>
import { computed, ref, onMounted, onBeforeUnmount } from 'vue'
import { useRoute } from 'vue-router'
import { getBetById, placeBet } from '../services/api'
import { useAuth } from '../composables/useAuth'
import { useBetCountdown } from '../composables/useBetCountdown'
import ChatView from "@/views/ChatView.vue";
import BetMyStakesTable from "@/views/BetMyStakesTable.vue"

// Composables und globale Zustände
const route = useRoute()
const { user, refreshAuth } = useAuth()
const { formatCountdown, getRemainingMs } = useBetCountdown()

const bet = ref(null)
const loading = ref(true)
const saving = ref(false)
const error = ref('')
const actionError = ref('')
const successMessage = ref('')
const balance = ref('')
const selectedPosition = ref(null)
const isChatVisible = ref(false)
const chatSectionRef = ref(null)

let reloadTimer = null

// Berechnete Eigenschaft, um zu prüfen, ob ein Benutzer eingeloggt ist.
const isLoggedIn = computed(() => !!user.value)

// Berechnete Eigenschaft, um den Titel der gewinnenden Position zu ermitteln.
const resultTitle = computed(() => {
  if (!bet.value) return ''
  const resultId = bet.value.resultPositionId
  const match = bet.value.positions?.find((p) => p.id === resultId)
  return match?.title || `Position #${resultId}`
})

// Berechnete Eigenschaft, um die CSS-Klasse für das Ergebnis zu bestimmen (gewonnen/verloren).
const resultClass = computed(() => {
  if (!bet.value?.settled) return ''
  return String(bet.value.resultPositionId) === String(selectedPosition.value?.id) ? 'win' : 'lose'
})

// Berechnete Eigenschaft, die den Countdown-Text formatiert.
const countdownText = computed(() => {
  if (!bet.value?.runningUntil) return ''
  return formatCountdown(bet.value.runningUntil)
})

/**
 * Berechnete Eigenschaft, die die Wetten des Benutzers für diese Wette anreichert.
 * Fügt den Klartext-Titel der Position zu jedem Einsatz hinzu, da das Backend
 * oft nur die `position_id` sendet.
 * Behandelt sowohl `positionId` (camelCase) als auch `position_id` (snake_case).
 */
const myEnrichedBets = computed(() => {
  if (!bet.value?.myBets || !bet.value?.positions) {
    return []
  }
  return bet.value.myBets.map((userBet) => {
    const posId = userBet.positionId ?? userBet.position_id
    const position = bet.value.positions.find((p) => p.id === posId)
    return {
      ...userBet,
      title: position ? position.title : `Position #${posId ?? 'Unbekannt'}`,
    }
  })
})

// Berechnete Eigenschaft, die prüft, ob der Benutzer bereits auf die aktuell ausgewählte Position gesetzt hat.
const hasPlacedOnSelectedPosition = computed(() => {
  if (!bet.value || !selectedPosition.value) return false
  return myEnrichedBets.value.some((placedBet) => {
    const posId = placedBet.positionId ?? placedBet.position_id
    return posId === selectedPosition.value.id
  })
})

// Berechnete Eigenschaft, die prüft, ob das Setzen auf eine *neue* Position verboten ist.
// Dies ist der Fall, wenn die Wette keine Mehrfacheinsätze erlaubt und der Benutzer schon gesetzt hat.
const isNewPositionDisallowed = computed(() => {
  if (!bet.value || !selectedPosition.value) return false
  if (bet.value.allowMultipleBets === true) return false
  return myEnrichedBets.value.length > 0
});

/**
 * Formatiert einen numerischen Wert als Geldbetrag.
 * @param {number | string} value - Der zu formatierende Wert.
 */
const formatMoney = (value) => {
  const num = Number(value || 0)
  return `${num.toFixed(2)} €`
}

const isBettingDisallowed = computed(() => {
  if (hasPlacedOnSelectedPosition.value) return true
  if (isNewPositionDisallowed.value) return true
  return false
})

// Berechnete Eigenschaft, die den Grund zurückgibt, warum das Wetten nicht erlaubt ist.
const bettingDisallowedReason = computed(() => {
  if (hasPlacedOnSelectedPosition.value) return 'Du hast bereits auf diese Position gesetzt.'
  if (isNewPositionDisallowed.value) return 'Du kannst bei dieser Wette nur auf eine Position setzen.'
  return ''
})

/**
 * Berechnete Eigenschaft, die die Teilnehmerliste einer abgerechneten Wette aufbereitet.
 * - Fügt den Gesamteinsatz und den Gewinnerstatus zu jedem Teilnehmer hinzu.
 * - Fügt die Titel der gewählten Positionen hinzu.
 * - Sortiert die Liste, sodass Gewinner oben stehen.
 */
const enrichedParticipants = computed(() => {
  if (!bet.value?.settled || !bet.value.participants) {
    return []
  }

  const participantsData = bet.value.participants
  const resultId = bet.value.resultPositionId
  const positions = bet.value.positions || []

  const formatMoney = (value) => Number(value || 0).toFixed(2);

  return Object.entries(participantsData).map(([email, userBets]) => {
    const totalStake = userBets.reduce((sum, bet) => sum + (bet.balance || 0), 0)
    const isWinner = userBets.some(bet => (bet.position_id ?? bet.positionId) === resultId)

    const positionTitles = userBets.map(userBet => {
      const posId = userBet.position_id ?? userBet.positionId
      const position = positions.find(p => p.id === posId)
      return position ? position.title : `Position #${posId}`
    }).join(', ')

    return { email, totalStake: formatMoney(totalStake), isWinner, positionTitles }
  }).sort((a, b) => {
    // Winners on top
    if (a.isWinner && !b.isWinner) return -1;
    if (!a.isWinner && b.isWinner) return 1;
    // Then sort by stake descending
    return b.totalStake - a.totalStake;
  });
})

/**
 * Gibt die passende CSS-Klasse für eine Position zurück, abhängig vom Wettstatus.
 * @param {object} pos - Das Positionsobjekt.
 */
const positionClass = (pos) => {
  if (!bet.value?.settled) {
    return selectedPosition.value?.id === pos.id ? 'selected' : ''
  }

  return String(pos.id) === String(bet.value.resultPositionId) ? 'winner' : 'loser'
}

/**
 * Handler, um den Einsatz auf eine Position zu erhöhen/verdoppeln.
 * @param {object} entry - Der spezifische Einsatz, der erhöht werden soll.
 */
const handleIncreaseStake = async (entry) => {
  const posId = entry.positionId ?? entry.position_id
  const current = Number(entry.balance ?? entry.amount ?? 0)
  if (!posId || !current) return
  await placeBet(route.params.id, posId, current)
  await refreshAuth()
  await loadBet()
}

/**
 * Lädt die Daten für die spezifische Wette vom Backend.
 */
const loadBet = async () => {
  loading.value = true
  error.value = ''

  try {
    const response = await getBetById(route.params.id)
    bet.value = response.answer

    if (bet.value?.positions?.length && !selectedPosition.value) selectedPosition.value = bet.value.positions[0];
  } catch (e) {
    error.value = e.message || 'Wette konnte nicht geladen werden.'
    bet.value = null
  } finally {
    loading.value = false
  }
}

/**
 * Handler für das Absenden des Wettformulars.
 */
const handlePlaceBet = async () => {
  actionError.value = ''
  successMessage.value = ''

  if (!selectedPosition.value) {
    actionError.value = 'Bitte zuerst eine Position auswählen.'
    return
  }

  const amount = Number(balance.value)
  if (!amount || amount <= 0) {
    actionError.value = 'Bitte einen gültigen Einsatz eingeben.'
    return
  }

  saving.value = true
  try {
    await placeBet(route.params.id, selectedPosition.value.id, amount)

    successMessage.value = 'Wette erfolgreich gesetzt.'

    await refreshAuth()
    await loadBet()

    balance.value = ''
  } catch (e) {
    actionError.value = e.message || 'Wette konnte nicht gesetzt werden.'
  } finally {
    saving.value = false
  }
}

// Lifecycle-Hook: Wird ausgeführt, wenn die Komponente eingehängt wird.
onMounted(async () => {
  await loadBet()

  // IntersectionObserver einrichten, um die Sichtbarkeit des Chats zu verfolgen.
  const observer = new IntersectionObserver(
    ([entry]) => {
      // Wenn der Chat-Bereich sichtbar wird, setze das Flag und stoppe die Beobachtung.
      if (entry.isIntersecting) {
        isChatVisible.value = true
        observer.unobserve(chatSectionRef.value)
      }
    },
    { rootMargin: '200px' } // Beginnt mit dem Laden, wenn der Chat 200px entfernt ist.
  )

  if (chatSectionRef.value) {
    observer.observe(chatSectionRef.value)
  }

  // Ein Timer, der prüft, ob die Wette abgelaufen ist, um die Daten neu zu laden.
  reloadTimer = setInterval(() => {
    if (bet.value && bet.value.running && getRemainingMs(bet.value.runningUntil) <= 0) {
      void loadBet()
    }
  }, 1000)
})

// Lifecycle-Hook: Wird ausgeführt, bevor die Komponente zerstört wird, um den Timer zu bereinigen.
onBeforeUnmount(() => {
  if (reloadTimer) clearInterval(reloadTimer)
})
</script>

<style lang="scss" scoped>
.wette-page {
  max-width: 1000px;
  margin: 0 auto;
  padding: 2rem;
  padding-top: 5rem;
}

.bet-card {
  background: $card-background;
  border: 1px solid $border-color;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.06);
}

.description {
  line-height: 1.6;
}

.meta {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-top: 1rem;
  opacity: 0.85;
}

.countdown {
  font-family: monospace;
  font-weight: 700;
}

.result-box {
  margin-top: 1rem;
  padding: 0.9rem 1rem;
  border-radius: 10px;
  font-weight: 800;
  border: 1px solid transparent;
}

.result-box.win {
  background: rgba(25, 135, 84, 0.12);
  color: #198754;
  border-color: rgba(25, 135, 84, 0.25);
}

.result-box.lose {
  background: rgba(220, 53, 69, 0.12);
  color: #dc3545;
  border-color: rgba(220, 53, 69, 0.25);
}

.positions-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 0.75rem;
  margin-top: 1rem;
}

.position-card {
  text-align: left;
  border: 1px solid $border-color;
  background: transparent;
  color: $text-color;
  border-radius: 10px;
  padding: 1rem;
  cursor: pointer;
  transition: transform 0.15s ease, border-color 0.15s ease;

  &:hover {
    transform: translateY(-2px);
    border-color: $primary-color;
  }

  &.selected {
    border-color: $primary-color;
    box-shadow: 0 0 0 3px rgba($primary-color, 0.15);
  }

  &.winner {
    border-color: #198754;
    background: rgba(25, 135, 84, 0.12);
  }

  &.loser {
    opacity: 0.6;
  }

  &:disabled {
    cursor: not-allowed;
  }
}

.position-title {
  font-weight: 700;
}

.position-id {
  margin-top: 0.35rem;
  font-size: 0.85rem;
  opacity: 0.75;
}

.form-group {
  margin-bottom: 1rem;

  label {
    display: block;
    margin-bottom: 0.45rem;
    font-weight: 600;
  }

  input {
    width: 100%;
    padding: 0.8rem;
    border: 1px solid $border-color;
    border-radius: 8px;
    box-sizing: border-box;
  }
}

button[type="submit"] {
  width: 100%;
  padding: 0.9rem;
  border: none;
  border-radius: 8px;
  background: $primary-color;
  color: white;
  font-weight: 700;
  cursor: pointer;

  &:hover {
    background: $primary-color-hover;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.hint {
  padding: 1rem;
  border-radius: 10px;
  background: rgba($primary-color, 0.08);
}

.hint.error-hint {
  background: rgba(220, 53, 69, 0.12);
  color: #dc3545;
}

.state-box, .message {
  margin-top: 1rem;
  padding: 1rem;
  border-radius: 10px;
}

.state-box.error, .message.error {
  background: rgba(220, 53, 69, 0.12);
  color: #dc3545;
}

.message.success {
  background: rgba(25, 135, 84, 0.12);
  color: #198754;
}

.chat-container {
  max-height: 450px;
  overflow-y: auto;
}

.participants-section {
  h2 {
    margin-bottom: 1rem;
  }
}

.participants-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.participant-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  border-radius: 10px;
  border: 1px solid $border-color;
  background: rgba(255, 255, 255, 0.02);
}

.participant-info {
  display: flex;
  flex-direction: column;
}

.participant-email { font-weight: 600; }
.participant-position { font-size: 0.9rem; opacity: 0.8; margin-top: 0.25rem; }
.participant-stake { font-weight: 700; font-size: 1.1rem; }

.participant-winner {
  border-color: rgba(25, 135, 84, 0.4);
  background: rgba(25, 135, 84, 0.1);
  .participant-stake { color: #198754; }
}
.participant-loser { opacity: 0.7; }
</style>